import React, { useEffect, useState, useCallback } from 'react';
import type { ExamItem, ExamQuestion, Branch, ExamResult, ClassRoom } from '../../types/db';
import { GradeLevel } from '../../types/db';
import { fetchExams } from '../../services/academicService';
import {
  BrainCircuit as BrainIcon,
  Plus as PlusIcon,
  Trash2 as TrashIcon,
  Sparkles as SparklesIcon,
  Loader2 as LoaderIcon,
  X as XIcon,
  Send as SendIcon,
  Calendar,
  Save,
  CheckCircle,
  AlertCircle,
  BellRing,
  FileText,
  Upload,
  ListChecks,
  Layers,
  Timer,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { generateExamQuestions, structureExamFromText } from '../../services/geminiService';
import mammoth from 'mammoth';

export function ExamManager({ branchId = 'ALL' }: { branchId?: string }) {
  const [activeTab, setActiveTab] = useState<'PACKAGES' | 'RESULTS'>('PACKAGES');
  const [exams, setExams] = useState<ExamItem[]>([]);
  const [results, setResults] = useState<ExamResult[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputMode, setInputMode] = useState<'AI' | 'WORD'>('AI');
  const [wordFile, setWordFile] = useState<File | null>(null);

  const [formData, setFormData] = useState<Partial<ExamItem> & { exam_type?: string }>({
    subject: '',
    grade: GradeLevel.SMA_12_IPA,
    topic: '',
    status: 'DRAFT',
    questions: [] as ExamQuestion[],
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    duration: 60,
    branch_id: branchId === 'ALL' ? '' : branchId,
    class_id: '',
    results_publish_status: 'IMMEDIATE',
    results_published_at: '',
    exam_type: 'PERIODIC_EXAM',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchExams(branchId);
      setExams(data);

      const { data: resData } = await supabase
        .from('exam_results')
        .select('*')
        .order('submitted_at', { ascending: false });
      if (resData) setResults(resData);

      const { data: bData } = await supabase.from('branches').select('*');
      if (bData) setBranches(bData.filter((b) => b.id !== 'HQ' && b.id !== 'HEADQUARTERS'));

      const { data: cData } = await supabase.from('classes').select('*');
      if (cData) setClasses(cData);
    } catch (e) {
      /* console removed */
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableClasses = classes.filter((c) => c.branch_id === formData.branch_id);

  const handleAIDevelopment = async () => {
    if (!formData.subject || !formData.class_id || !formData.branch_id) {
      return window.alert('Mohon lengkapi Mapel, Cabang, dan Pilih Kelas sebelum memanggil AI.');
    }
    setLoading(true);
    try {
      const totalQ = formData.exam_type === 'DAILY_QUIZ' ? 5 : 10;
      const generated = await generateExamQuestions(
        formData.subject,
        formData.grade || 'Umum',
        formData.topic || 'Umum',
        totalQ
      );
      setFormData((prev) => ({ ...prev, questions: generated as ExamQuestion[] }));
      setIsEditing(true);
      window.alert(`Berhasil! Silakan tinjau ${totalQ} soal sebelum disimpan.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Gagal generate soal AI';
      window.alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleWordUpload = async () => {
    if (!wordFile || !formData.class_id || !formData.subject) {
      return window.alert('Lengkapi identitas ujian dan pilih file Word!');
    }

    setLoading(true);
    try {
      const arrayBuffer = await wordFile.arrayBuffer();
      let imageCounter = 0;
      const uploadedImageUrls: string[] = [];
      
      // Konfigurasi mammoth untuk mengekstrak gambar dari dokumen Word dan langsung mengunggahnya ke Supabase Storage
      const options = {
        convertImage: mammoth.images.imgElement(async (image) => {
          const buffer = await image.read();
          imageCounter++;
          const fileExt = image.contentType.split('/')[1] || 'png';
          const filename = `exam_${Date.now()}_${imageCounter}.${fileExt}`;
          const filePath = `question-images/${filename}`;

          const { error: uploadError } = await supabase.storage
            .from('learning-files')
            .upload(filePath, buffer, { contentType: image.contentType });

          if (uploadError) {
            console.error('Gagal upload gambar otomatis:', uploadError);
            throw new Error(`Upload gambar gagal: ${uploadError.message}`);
          }

          const { data: publicUrlData } = supabase.storage
            .from('learning-files')
            .getPublicUrl(filePath);

          if (!publicUrlData?.publicUrl) {
            throw new Error('URL publik gambar tidak berhasil dibuat.');
          }

          const publicUrl = publicUrlData.publicUrl;

          if (!publicUrl) {
            throw new Error('URL publik gambar tidak berhasil dibuat.');
          }

          uploadedImageUrls.push(publicUrl);
          console.log('Gambar berhasil diupload:', publicUrl);

          return { src: publicUrl };
        })
      };

      const result = await mammoth.convertToHtml({ arrayBuffer }, options);
      const htmlContent = result.value;

      console.log('HTML hasil Mammoth:', htmlContent);

      console.log('URL gambar hasil upload:', uploadedImageUrls);

      // Mengirim konten HTML ke service Gemini untuk membaca struktur soal.
      const structuredQuestions = await structureExamFromText(htmlContent);

      // Gabungkan soal hasil Gemini dengan URL gambar dari Supabase.
      const questionsWithImages = structuredQuestions.map((question, index) => ({
        ...question,
        image_url: uploadedImageUrls[index] || question.image_url || '',
      }));

      console.log('Soal final dengan gambar:', questionsWithImages);

      setFormData((prev) => ({
        ...prev,
        questions: questionsWithImages as ExamQuestion[],
      }));
      setIsEditing(true);
      setWordFile(null);
      window.alert(`Berhasil mengekstrak ${structuredQuestions.length} soal beserta gambarnya secara otomatis!`);
    } catch (err: any) {
      window.alert('Gagal: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExam = async (status: 'DRAFT' | 'PUBLISHED') => {
    if (!formData.branch_id || !formData.class_id || !formData.date || !formData.start_time) {
      return window.alert('Lengkapi data cabang, kelas, tanggal, dan jam mulai!');
    }
    if (!formData.questions || formData.questions.length === 0) {
      return window.alert('Belum ada soal untuk disimpan.');
    }

    setLoading(true);
    try {
      const examId = formData.id || 'EXM-' + Date.now();
      const selectedClass = classes.find((c) => c.id === formData.class_id);
      const payload = {
        ...formData,
        id: examId,
        status,
        grade: selectedClass?.name || formData.grade,
      };
      const { error } = await supabase.from('exams').upsert(payload);
      if (error) throw error;

      window.alert(`Paket ${formData.exam_type === 'DAILY_QUIZ' ? 'Daily Quiz' : 'Ujian'} berhasil disimpan sebagai ${status}!`);
      setIsFormOpen(false);
      setIsEditing(false);
      loadData();
    } catch (e) {
      window.alert('Gagal menyimpan: ' + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Hapus paket soal ini?')) {
      try {
        setLoading(true);
        const { error } = await supabase.from('exams').delete().eq('id', id);
        if (error) throw error;
        loadData();
      } catch (e) {
        window.alert('Gagal hapus');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="space-y-6 animate-fade-in font-sans pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
            <BrainIcon className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-gray-900 uppercase">Akademik: Ujian & Daily Quiz AI</h2>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
              Manajemen Paket Soal & Kuis Harian
            </p>
          </div>
        </div>
        <div className="flex bg-white p-1 rounded-2xl border border-slate-100 shadow-sm">
          <button
            onClick={() => setActiveTab('PACKAGES')}
            className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'PACKAGES' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
          >
            Paket Soal & Kuis
          </button>
          <button
            onClick={() => setActiveTab('RESULTS')}
            className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'RESULTS' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
          >
            Hasil Pengerjaan
          </button>
        </div>
      </div>

      {activeTab === 'PACKAGES' ? (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setFormData({
                  subject: '',
                  status: 'DRAFT',
                  questions: [] as ExamQuestion[],
                  branch_id: branchId !== 'ALL' ? branchId : '',
                  class_id: '',
                  date: new Date().toISOString().split('T')[0],
                  start_time: '08:00',
                  duration: 60,
                  results_publish_status: 'IMMEDIATE',
                  exam_type: 'PERIODIC_EXAM',
                });
                setIsFormOpen(true);
                setIsEditing(false);
              }}
              className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase shadow-xl hover:bg-indigo-700 transition-all flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" /> Buat Paket Soal Baru
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exams.map((e: any) => {
              const targetClass = classes.find((c) => c.id === e.class_id);
              const isDaily = e.exam_type === 'DAILY_QUIZ';
              return (
                <div
                  key={e.id}
                  className="p-8 bg-white border border-gray-100 rounded-[3rem] group hover:border-indigo-100 transition-all shadow-sm relative"
                >
                  <div className="flex items-center gap-2 absolute top-6 right-6">
                    <span className={`px-2.5 py-1 rounded-full text-[7px] font-black uppercase tracking-widest ${isDaily ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' : 'bg-purple-50 text-purple-600'}`}>
                      {isDaily ? 'Daily Quiz' : 'Ujian Berkala'}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-[7px] font-black uppercase tracking-widest ${e.status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                      {e.status}
                    </span>
                  </div>
                  <h4 className="font-black text-indigo-900 text-xl uppercase mb-1">{e.subject}</h4>
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-3">{e.topic || 'Tanpa Topik Spesifik'}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <div className="flex items-center gap-1.5 text-[9px] font-black text-indigo-600 uppercase bg-indigo-50 px-2.5 py-1 rounded-lg">
                      <Layers className="w-3 h-3" /> {targetClass?.name || e.grade}
                    </div>
                    <div className="flex items-center gap-1.5 text-[9px] font-black text-slate-400 uppercase bg-slate-50 px-2.5 py-1 rounded-lg">
                      <Calendar className="w-3 h-3" /> {e.date}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setFormData(e);
                        setIsFormOpen(true);
                        setIsEditing(true);
                      }}
                      className="flex-1 bg-slate-50 text-slate-600 py-3 rounded-xl font-black text-[10px] uppercase hover:bg-indigo-50 transition-all"
                    >
                      Review Soal ({e.questions?.length || 0})
                    </button>
                    <button
                      onClick={() => handleDelete(e.id)}
                      className="p-3 bg-rose-50 text-rose-400 rounded-xl hover:bg-rose-500 hover:text-white transition-all"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-[3rem] shadow-sm border border-slate-100 overflow-hidden animate-fade-in">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest">
              <tr>
                <th className="p-6 text-left">Nama Siswa</th>
                <th className="p-6 text-left">Mata Pelajaran</th>
                <th className="p-6 text-center">Benar</th>
                <th className="p-6 text-center">Skor</th>
                <th className="p-6 text-center">Submit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
              {results.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-6 uppercase">{r.student_name}</td>
                  <td className="p-6 uppercase text-indigo-600">
                    {exams.find((x) => x.id === r.exam_id)?.subject || 'UJIAN'}
                  </td>
                  <td className="p-6 text-center text-slate-400">
                    {r.correct_answers} / {r.total_questions}
                  </td>
                  <td className="p-6 text-center">
                    <span
                      className={`px-4 py-2 rounded-xl font-black text-xs ${r.score >= 75 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}
                    >
                      {r.score}
                    </span>
                  </td>
                  <td className="p-6 text-center text-slate-300 font-mono text-[9px]">
                    {new Date(r.submitted_at).toLocaleString('id-ID')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[200] flex items-center justify-center p-4 md:p-8 backdrop-blur-md overflow-y-auto">
          <div className="bg-white rounded-[3.5rem] w-full max-w-6xl shadow-2xl relative my-auto animate-fade-in-up border border-white overflow-hidden flex flex-col max-h-[95vh]">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shadow-inner">
                  <SparklesIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black text-xl text-slate-900 uppercase tracking-tighter leading-none">
                    {isEditing ? 'Editor Paket Soal & Kuis' : 'Setup Paket Soal Baru'}
                  </h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                    Delta Integrated Exam & Quiz System
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsFormOpen(false)}
                className="p-3 bg-slate-50 text-slate-400 hover:text-slate-900 rounded-full transition-all"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 md:p-12 no-scrollbar bg-slate-50/30">
              {!isEditing ? (
                <div className="max-w-3xl mx-auto space-y-12 py-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                      Kategori / Jenis Soal
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, exam_type: 'PERIODIC_EXAM', duration: 60 })}
                        className={`p-5 rounded-2xl font-black text-xs uppercase tracking-wider border-2 transition-all flex items-center justify-center gap-2 ${formData.exam_type === 'PERIODIC_EXAM' ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-200'}`}
                      >
                        Ujian Berkala (3-6 Bulan)
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, exam_type: 'DAILY_QUIZ', duration: 15, questions: [] })}
                        className={`p-5 rounded-2xl font-black text-xs uppercase tracking-wider border-2 transition-all flex items-center justify-center gap-2 ${formData.exam_type === 'DAILY_QUIZ' ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-200'}`}
                      >
                        Daily Quiz Harian (5 Soal)
                      </button>
                    </div>
                  </div>

                  <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-inner">
                    <button
                      onClick={() => setInputMode('AI')}
                      className={`flex-1 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${inputMode === 'AI' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
                    >
                      <SparklesIcon className="w-4 h-4" /> Generator AI
                    </button>
                    <button
                      onClick={() => setInputMode('WORD')}
                      className={`flex-1 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${inputMode === 'WORD' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
                    >
                      <FileText className="w-4 h-4" /> Impor Word (.docx + Gambar Otomatis)
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-[10px] font-black text-indigo-600 uppercase border-b pb-2 tracking-[0.2em] flex items-center gap-2">
                        <Layers className="w-3.5 h-3.5" /> 1. Target Cabang & Kelas
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-400 uppercase ml-2">
                            Pilih Cabang
                          </label>
                          <select
                            className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white shadow-sm text-xs outline-none focus:border-indigo-100"
                            value={formData.branch_id}
                            onChange={(e) =>
                              setFormData({ ...formData, branch_id: e.target.value, class_id: '' })
                            }
                          >
                            <option value="">-- Pilih Cabang --</option>
                            {branches
                              .filter((b) => b.is_active !== false || b.id === formData.branch_id)
                              .map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.name.toUpperCase()}
                                  {b.is_active === false ? ' (NONAKTIF)' : ''}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-400 uppercase ml-2">
                            Pilih Target Kelas
                          </label>
                          <select
                            className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white shadow-sm text-xs outline-none focus:border-indigo-100 disabled:opacity-50"
                            value={formData.class_id}
                            onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                            disabled={!formData.branch_id}
                          >
                            <option value="">-- Cari Kelas --</option>
                            {availableClasses.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name.toUpperCase()}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <p className="text-[10px] font-black text-indigo-600 uppercase border-b pb-2 tracking-[0.2em] flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5" /> 2. Waktu & Durasi
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-400 uppercase ml-2">
                            Tanggal
                          </label>
                          <input
                            type="date"
                            className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white shadow-sm text-xs outline-none"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-slate-400 uppercase ml-2">
                            Jam Mulai
                          </label>
                          <input
                            type="time"
                            className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white shadow-sm text-xs outline-none"
                            value={formData.start_time}
                            onChange={(e) =>
                              setFormData({ ...formData, start_time: e.target.value })
                            }
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-400 uppercase ml-2">
                          Durasi (Menit)
                        </label>
                        <div className="relative">
                          <Timer className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                          <input
                            type="number"
                            className="w-full border-2 border-white pl-12 pr-4 py-4 rounded-2xl font-black bg-white shadow-sm text-xs outline-none"
                            value={formData.duration}
                            onChange={(e) =>
                              setFormData({ ...formData, duration: Number(e.target.value) })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <p className="text-[10px] font-black text-indigo-600 uppercase border-b pb-2 tracking-[0.2em] flex items-center gap-2">
                      <SparklesIcon className="w-3.5 h-3.5" /> 3. Konten Soal {formData.exam_type === 'DAILY_QUIZ' ? '(Target: 5 Soal)' : ''}
                    </p>
                    <div className="space-y-4">
                      <input
                        className="w-full border-2 border-white p-5 rounded-2xl font-black uppercase bg-white shadow-sm text-xs outline-none focus:border-indigo-100"
                        placeholder="MATA PELAJARAN (MISAL: MATEMATIKA)"
                        value={formData.subject}
                        onChange={(e) =>
                          setFormData({ ...formData, subject: e.target.value.toUpperCase() })
                        }
                      />
                      {inputMode === 'AI' ? (
                        <>
                          <input
                            className="w-full border-2 border-white p-5 rounded-2xl font-bold bg-white shadow-sm text-xs outline-none"
                            placeholder="Topik Spesifik / Bab Materi"
                            value={formData.topic}
                            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                          />
                          <button
                            onClick={handleAIDevelopment}
                            disabled={loading}
                            className="w-full bg-indigo-600 text-white py-6 rounded-3xl font-black uppercase text-xs tracking-[0.2em] shadow-2xl hover:bg-indigo-700 transition-all flex justify-center items-center gap-4"
                          >
                            {loading ? (
                              <LoaderIcon className="animate-spin w-6 h-6" />
                            ) : (
                              <SparklesIcon className="w-6 h-6" />
                            )}{' '}
                            {loading ? 'SINKRONISASI AI...' : formData.exam_type === 'DAILY_QUIZ' ? 'GENERATE 5 SOAL DAILY QUIZ' : 'GENERATE BANK SOAL UJIAN'}
                          </button>
                        </>
                      ) : (
                        <div className="space-y-6">
                          <label className="border-4 border-dashed border-slate-100 rounded-[3rem] p-16 flex flex-col items-center justify-center gap-5 cursor-pointer hover:bg-white hover:border-indigo-200 transition-all text-slate-300 group shadow-inner">
                            <input
                              type="file"
                              className="hidden"
                              accept=".docx"
                              onChange={(e) => setWordFile(e.target.files?.[0] || null)}
                            />
                            {wordFile ? (
                              <div className="text-center animate-fade-in">
                                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                                <p className="text-slate-900 font-black text-sm uppercase truncate max-w-[300px]">
                                  {wordFile.name}
                                </p>
                              </div>
                            ) : (
                              <>
                                <Upload className="w-16 h-16 text-slate-200 group-hover:scale-110 transition-all" />
                                <p className="font-black text-xs uppercase tracking-widest text-slate-400">
                                  Pilih File .docx (Microsoft Word dengan Gambar)
                                </p>
                              </>
                            )}
                          </label>
                          <button
                            onClick={handleWordUpload}
                            disabled={loading || !wordFile}
                            className="w-full bg-slate-900 text-white py-6 rounded-3xl font-black uppercase text-xs tracking-[0.2em] shadow-2xl hover:bg-black transition-all flex justify-center items-center gap-4"
                          >
                            {loading ? (
                              <LoaderIcon className="animate-spin w-6 h-6" />
                            ) : (
                              <FileText className="w-6 h-6" />
                            )}{' '}
                            EKSTRAK SOAL & GAMBAR OTOMATIS
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-12 animate-fade-in pb-10">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    <div className="lg:col-span-2 space-y-10">
                      <div className="flex justify-between items-center px-4">
                        <h4 className="font-black text-xl text-slate-900 uppercase tracking-tight flex items-center gap-3">
                          <ListChecks className="w-6 h-6 text-indigo-600" /> Review Soal ({formData.questions?.length || 0})
                        </h4>
                        <button
                          onClick={() =>
                            setFormData({
                              ...formData,
                              questions: [
                                ...(formData.questions || []),
                                { question: '', options: ['', '', '', ''], correctAnswer: '' },
                              ],
                            })
                          }
                          className="bg-indigo-50 text-indigo-600 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase hover:bg-indigo-600 hover:text-white transition-all"
                        >
                          + TAMBAH SOAL
                        </button>
                      </div>
                      <div className="space-y-10">
                        {formData.questions?.map((q, idx) => (
                          <div
                            key={idx}
                            className="p-10 bg-white border border-slate-100 rounded-[3.5rem] space-y-8 shadow-sm group relative hover:border-indigo-100 transition-all"
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-4">
                                <span className="w-12 h-12 bg-slate-900 text-white rounded-[1.5rem] flex items-center justify-center font-black text-sm shadow-xl">
                                  #{idx + 1}
                                </span>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                  Question Editor
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  const n = [...(formData.questions || [])];
                                  n.splice(idx, 1);
                                  setFormData({ ...formData, questions: n });
                                }}
                                className="p-3 text-rose-200 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>

                            <textarea
                              className="w-full border-2 border-slate-50 p-6 rounded-[2rem] font-bold bg-slate-50/30 outline-none text-sm leading-relaxed min-h-[140px] focus:border-indigo-100"
                              value={q.question}
                              onChange={(e) => {
                                const n = [...(formData.questions || [])];
                                n[idx].question = e.target.value;
                                setFormData({ ...formData, questions: n });
                              }}
                            />

                            {/* Pratinjau & Input URL Gambar Soal */}
                            <div className="space-y-3 bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                              <label className="text-[9px] font-black text-slate-400 uppercase">
                                Gambar Soal (URL / Public Link)
                              </label>
                              <input
                                type="text"
                                className="w-full border-2 border-slate-200 p-3 rounded-xl text-[11px] font-bold bg-white outline-none focus:border-indigo-200"
                                placeholder="Link gambar otomatis dari Word..."
                                value={q.image_url || ''}
                                onChange={(e) => {
                                  const n = [...(formData.questions || [])];
                                  n[idx].image_url = e.target.value;
                                  setFormData({ ...formData, questions: n });
                                }}
                              />
                              {q.image_url && (
                                <div className="mt-3 relative w-40 h-40 rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                                  <img 
                                    src={q.image_url} 
                                    alt={`Ilustrasi Soal #${idx + 1}`} 
                                    className="object-contain w-full h-full" 
                                  />
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {q.options.map((opt, oIdx) => (
                                <input
                                  key={oIdx}
                                  className="w-full border-2 border-slate-50 p-4 rounded-2xl text-[11px] font-bold bg-slate-50/20 outline-none focus:border-indigo-200"
                                  placeholder={`Pilihan ${String.fromCharCode(65 + oIdx)}`}
                                  value={opt}
                                  onChange={(e) => {
                                    const n = [...(formData.questions || [])];
                                    n[idx].options[oIdx] = e.target.value;
                                    setFormData({ ...formData, questions: n });
                                  }}
                                />
                              ))}
                            </div>
                            <div className="bg-emerald-50 p-6 rounded-[2rem] border border-emerald-100 flex flex-col md:flex-row items-center gap-6">
                              <div className="flex items-center gap-3 text-[10px] font-black text-emerald-700 uppercase tracking-widest shrink-0">
                                <CheckCircle className="w-5 h-5" /> Kunci Jawaban Benar
                              </div>
                              <select
                                value={q.correctAnswer}
                                onChange={(e) => {
                                  const n = [...(formData.questions || [])];
                                  n[idx].correctAnswer = e.target.value;
                                  setFormData({ ...formData, questions: n });
                                }}
                                className="w-full p-4 rounded-2xl border-2 border-emerald-200 bg-white font-black text-emerald-800 outline-none text-xs"
                              >
                                <option value="">-- Pilih Jawaban --</option>
                                {q.options.map((o) => (
                                  <option key={o} value={o}>
                                    {o}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-8">
                      <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm sticky top-0">
                        <h4 className="font-black text-lg text-slate-900 uppercase tracking-tighter mb-8 flex items-center gap-3">
                          <BellRing className="w-6 h-6 text-indigo-600" /> Finalisasi Paket
                        </h4>
                        <div className="space-y-6">
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                              Rilis Hasil Ke Siswa
                            </label>
                            <select
                              className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black text-[10px] uppercase bg-slate-50/50 outline-none"
                              value={formData.results_publish_status}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  results_publish_status: e.target.value as any,
                                })
                              }
                            >
                              <option value="IMMEDIATE">🚀 Langsung Setelah Submit</option>
                              <option value="SCHEDULED">⏰ Sesuai Jadwal (Backdate)</option>
                            </select>
                          </div>
                          <div className="pt-8 border-t border-slate-50 space-y-4">
                            <button
                              onClick={() => handleSaveExam('PUBLISHED')}
                              className="w-full bg-indigo-600 text-white py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-2xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-3"
                            >
                              <SendIcon className="w-5 h-5" /> PUBLISH SEKARANG
                            </button>
                            <button
                              onClick={() => handleSaveExam('DRAFT')}
                              className="w-full bg-slate-900 text-white py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-2xl hover:bg-black transition-all flex items-center justify-center gap-3"
                            >
                              <Save className="w-5 h-5" /> SIMPAN SEBAGAI DRAFT
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="bg-amber-50 p-8 rounded-[2.5rem] border border-amber-100 flex items-start gap-4">
                        <AlertCircle className="w-6 h-6 text-amber-500 shrink-0 mt-1" />
                        <p className="text-[11px] text-amber-800 font-bold leading-relaxed uppercase tracking-tight">
                          Pastikan seluruh identitas dan jumlah soal sudah sesuai dengan jenis ujian yang dipilih.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
